import React, { useRef } from 'react';
import { ThumbsUp, Meh, ThumbsDown } from 'lucide-react';
import { C } from '../../styles/tokens';

export type QualityRating = 'good' | 'satisfactory' | 'bad';

interface AudioQualityRatingProps {
  // Fired once, when the user picks a rating for this asset.
  onRate: (rating: QualityRating) => void;
}

const OPTIONS: { rating: QualityRating; label: string; Icon: typeof ThumbsUp; color: string }[] = [
  { rating: 'good', label: 'Good', Icon: ThumbsUp, color: C.g500 },
  { rating: 'satisfactory', label: 'Okay', Icon: Meh, color: C.a500 },
  { rating: 'bad', label: 'Bad', Icon: ThumbsDown, color: C.r500 },
];

// Compact Good/Okay/Bad control. Presentational: it reports the pick via onRate
// and the host (QualityRatingPrompt) handles the thank-you/dismiss. The ref guard
// keeps a rapid double-click from firing onRate twice before the host unmounts it.
const AudioQualityRating: React.FC<AudioQualityRatingProps> = ({ onRate }) => {
  const firedRef = useRef(false);

  const handleSelect = (rating: QualityRating) => {
    if (firedRef.current) return;
    firedRef.current = true;
    onRate(rating);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: C.n500 }}>Rate quality:</span>
      {OPTIONS.map(({ rating, label, Icon, color }) => (
        <button
          key={rating}
          type="button"
          title={label}
          onClick={(e) => { e.stopPropagation(); handleSelect(rating); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 9px',
            borderRadius: 999,
            border: `1px solid ${C.n200}`,
            background: C.w,
            fontSize: 11,
            fontWeight: 600,
            color: C.n600,
            cursor: 'pointer',
            lineHeight: 1.6,
          }}
        >
          <Icon size={12} color={color} />
          {label}
        </button>
      ))}
    </div>
  );
};

export default AudioQualityRating;
